import { skipToken } from '@reduxjs/toolkit/query';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import Loader from 'components/Loader';
import {
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import TabsGallery, { TabType } from 'components/TabsGallery';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery } from 'store/api/projects';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useLeaveDeletedEntity } from 'utils/useNavigateWithReturnState';
import ProjectPageHeader from './ProjectPageHeader';

import CasesView from './views/CasesView';
import ExpensesView from './views/ExpensesView';
import GeneralView from './views/GeneralView';
import InvoicesView from './views/InvoicesView';
import TimecardsView from './views/TimecardsView';

const ProjectPage = () => {
    const { id: projectId } = useParams();
    const isNew = useIsNewDesign();
    const canUpdate = useHasPermissions(['UPDATE.PROJECTS']);

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const { data: project, isLoading } = useGetProjectByIdQuery(projectId ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });
    const isDraft = !!project?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/projects');
    const draftActions = useDraftActions({
        draft: project?.isDraft ? { type: 'project', id: project.id } : null,
        createRoute: '/projects/new',
        onDeleted: leaveDeleted,
    });

    const tabComponents = useMemo(
        (): TabType[] =>
            project
                ? [
                      {
                          id: 'general',
                          label: t('projects.tabs.general'),
                          component: <GeneralView project={project} />,
                      },
                      {
                          id: 'cases',
                          label: t('projects.tabs.cases'),
                          component: <CasesView project={project} />,
                      },
                      // A draft has no time, expenses or invoices of its own.
                      {
                          id: 'timecards',
                          label: t('projects.tabs.timecards'),
                          hidden: isDraft,
                          component: <TimecardsView project={project} />,
                      },
                      {
                          id: 'expenses',
                          label: t('projects.tabs.expenses'),
                          hidden: isDraft,
                          component: <ExpensesView project={project} />,
                      },
                      {
                          id: 'invoices',
                          label: t('projects.tabs.invoices'),
                          hidden: isDraft,
                          component: <InvoicesView project={project} />,
                      },
                  ]
                : [],
        [project, isDraft]
    );

    if (isLoading) {
        return <Loader />;
    }

    if (project && isNew) {
        return (
            <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
                <RecordPage header={<ProjectPageHeader project={project} />}>
                    <SectionEditProvider canEdit={canUpdate && !isDraft}>
                        <TabsGallery
                            tabs={tabComponents}
                            tabGroupId="project-page"
                            trackTabsInHistoryState
                            usePathBasedRouting
                            wrapperClassName={RECORD_TABS_CLASS}
                            panelClassName={RECORD_PANEL_CLASS}
                            className={RECORD_TAB_STRIP_CLASS}
                        />
                    </SectionEditProvider>
                </RecordPage>
                {draftActions.shareDialog}
            </DraftActionsContext.Provider>
        );
    }

    // The header and the General view read the draft from here.
    return project ? (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <PaddedLayout>
                <ProjectPageHeader project={project} />
                <TabsGallery
                    tabs={tabComponents}
                    tabGroupId="project-page"
                    trackTabsInHistoryState
                    usePathBasedRouting
                />
            </PaddedLayout>
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    ) : (
        <></>
    );
};

export default ProjectPage;
